// react
import d3 = require("d3");
import React = require("react");
import ReactFauxDOM = require("react-faux-dom");

// local
import {Loader} from "../../../components/Utils/loader/Loader";
import {WidgetResponse} from "../../DashboardContainer";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {WidgetBadge, WidgetBadgeType} from "../DashboardWidget";
import AutoSizer from "react-virtualized-auto-sizer";

declare var AppTranslations: AppTranslations;

interface DispenseTimeWidgetResponse {
    lowerLimit: number;
    upperLimit: number;
    value: number;
}

interface DispenseTimeWidgetProps {
    data: WidgetResponse;
    title: string;
}

interface DispenseTimeWidgetState {
    response: DispenseTimeWidgetResponse;

    barWidth: number;
    // the total number of bars(arcs)
    numSections: number;
    // the percentage of each bar
    sectionPerc: number;
    // padding between each bar
    padRad: number;
    chartInset: number;
    totalPercent: number;
    // the needle element of the gauge
    needle: Needle;
    // the minimum value of the gauge
    absMin: number;
    // the maximum value of the gauge
    absMax: number;
    // available colors
    sectionColors: string[];
    // unit of response
    unitOfMeasure: string;
}

export class DispenseTimeWidget extends React.Component<DispenseTimeWidgetProps, DispenseTimeWidgetState> {

    constructor(props: DispenseTimeWidgetProps) {
        super(props);

        this.state = {
            response: undefined,
            barWidth: 15,
            numSections: undefined,
            sectionPerc: undefined,
            padRad: 0,
            chartInset: 0,
            totalPercent: 0.75,
            needle: undefined,
            absMin: undefined,
            absMax: undefined,
            sectionColors: [
                "rgba(233, 34, 19, 0.7019607843137254)",  // red
                "rgba(233, 34, 19, 0.7019607843137254)",  // red
                "rgba(255, 153, 38, 0.7019607843137254)", // orange
                "rgba(105, 255, 79, 0.7019607843137254)", // green
                "rgba(255, 153, 38, 0.7019607843137254)", // orange
                "rgba(233, 34, 19, 0.7019607843137254)",  // red
                "rgba(233, 34, 19, 0.7019607843137254)",   // red
            ],
            unitOfMeasure: "s",
        };

        this.retrieveData();
    }

    public getD3Graph(size) {
        const div = new ReactFauxDOM.Element("div");
        const sizeFactor = size.width ? Math.min(size.width, size.height) : 410;

        const margin = {top: 0, right: 0, bottom: 0, left: 0};
        const width: number = sizeFactor;
        const height: number = sizeFactor * 4 / 5;
        const radius: number = Math.min(width, height) / 2;

        const svg = d3.select(div)
            .append("svg")
            .attr("width", width + margin.left + margin.right)
            .attr("height", height + margin.top + margin.bottom);

        const chart = svg.append("g")
            .attr("transform",
                "translate(" + ((width + margin.left) / 2) + ", " + ((height + margin.top) / 2 + height * 0.2) + ")");

        const arc = d3.arc()
            .outerRadius(radius + 5)
            .innerRadius(0)
            .startAngle(0 - Math.PI / 2)
            .endAngle(Math.PI - Math.PI / 2);

        chart.append("path")
            .attr("class", "arc")
            .attr("d", arc)
            .attr("fill", "#f5f5f5");

        let totalPercent = this.state.totalPercent;
        for (let sectionIndex = 1; sectionIndex <= this.state.numSections; sectionIndex++) {
            const arcStartRad: number = AngleConverter.percToRad(totalPercent);
            const arcEndRad: number = arcStartRad + AngleConverter.percToRad(this.state.sectionPerc);

            totalPercent += this.state.sectionPerc;

            const startPadRad: number = this.state.padRad / 2;
            let endPadRad: number = this.state.padRad / 2;

            if (sectionIndex === this.state.numSections) {
                endPadRad = 0;
            }

            const arc = d3.arc()
                .outerRadius(radius - this.state.chartInset)
                .innerRadius(radius - this.state.chartInset - this.state.barWidth)
                .startAngle(arcStartRad + startPadRad)
                .endAngle(arcEndRad - endPadRad);

            chart.append("path")
                .attr("fill", this.state.sectionColors[sectionIndex - 1])
                .attr("d", arc);
        }

        // TICK VALUES
        for (let i = 0; i <= this.state.numSections; i++) {
            const perc: number = i * (1 / this.state.numSections);
            const thetaRad: number = AngleConverter.percToRad(perc / 2);
            let topX: number = -radius * Math.cos(thetaRad) * 1.05;
            if (this.state.numSections / 2 > i) {
                topX -= 20;
            }
            const topY: number = -radius * Math.sin(thetaRad) * 1.05;
            chart.append("text")
                .attr("x", topX)
                .attr("y", topY)
                .attr("fill", "darkgray")
                .attr("class", "gaugeTick")
                .text(this.getTickValue(i) + this.state.unitOfMeasure);
        }

        const needle = new Needle(chart, sizeFactor / 3.3, sizeFactor / 25);
        this.setValue(needle, chart, this.state.response.value);

        div.setAttribute("class", "absolute-center")

        return div.toReact();
    }

    public getTickValue(i: number): number {
        const sectionPortion: number = (this.state.absMax - this.state.absMin) / this.state.numSections;
        return this.state.absMin + sectionPortion * i;
    }

    public render() {
        if (!this.state.response) {
            return (
                <div className="dispense-time-widget">
                    <Loader/>
                </div>
            );
        }
        return (
            <VeBoxCard className="dispense-time-widget home-dashboard-card">
                <VeBoxCard.Header
                    className="dispense-time-widget__header space-between"
                    title={this.props.title.toUpperCase()}
                    titleClassName={"lowercase capitalize"}
                    singleLineTitle={true}
                    back={
                        <WidgetBadge type={WidgetBadgeType.COMMERCIAL}/>
                    }
                />
                <VeBoxCard.Body className="dispense-time-widget__container">
                    <AutoSizer style={{width: "auto"}}>
                        {({width, height}) => <div className="dispense-time-widget__chart">
                            {this.getD3Graph({width, height})}
                        </div>}
                    </AutoSizer>
                </VeBoxCard.Body>
            </VeBoxCard>
        );
    }

    public setValue(needle: Needle, chart, value: number): void {
        let tValue = value;
        if (tValue == null) {
            tValue = 0;
        }
        let percentage: number = (tValue - this.state.absMin) / (this.state.absMax - this.state.absMin);
        if (percentage > 1) {
            percentage = 1;
        }
        if (percentage < 0) {
            percentage = 0;
        }

        if (value) {
            needle.updateTextValue(
                `${parseFloat("" + tValue).toFixed(2)} ${this.state.unitOfMeasure}`,
            );
        } else {
            needle.updateTextValue("N/D");
        }

        needle.drawOn(percentage, tValue, this.state.unitOfMeasure);
        needle.animateOn(percentage);
    }

    private retrieveData(): void {
        const dataUrl: string = this.props.data.dataUrl;
        Ajax.vbxAjax({
            url: dataUrl,
            type: "GET",
            cache: false,
            success: (response: DispenseTimeWidgetResponse) => {
                const colorsLen: number = this.state.sectionColors.length;
                this.setState({
                    response,
                    numSections: colorsLen,
                    sectionPerc: 1 / colorsLen / 2,
                    absMin: response.lowerLimit - (response.upperLimit - response.lowerLimit) * ((colorsLen - 1) / 2),
                    absMax: response.upperLimit + (response.upperLimit - response.lowerLimit) * ((colorsLen - 1) / 2),
                });
            },
        });
    }
}

export class AngleConverter {
    /**
     *  Transform a percentage to degrees
     *
     * @param perc
     * @returns {number}
     */
    public static percToDeg(perc: number): number {
        return perc * 360;
    }

    /**
     *  transform degrees to radiant
     *
     * @param deg
     * @returns {number}
     */
    public static degToRad(deg: number): number {
        return deg * Math.PI / 180;
    }

    /**
     *  Transform percentage to radiant
     *
     * @param perc
     * @returns {number}
     */
    public static percToRad(perc: number): number {
        return AngleConverter.degToRad(AngleConverter.percToDeg(perc));
    }
}

export class Needle {
    // the text element under the needle
    private valueText: JQuery;

    /**
     *  Instantiate a needle
     *
     * @param parent the parent component
     * @param {number} len the needle length
     * @param {number} radius the needle center radius
     */
    public constructor(protected parent, protected len: number, protected radius: number) {
    }

    /**
     *  Draw the needle in the parent element
     *
     * @param perc
     * @param tValue
     * @param unitOfMeasure
     */
    public drawOn(perc: number, tValue: number, unitOfMeasure: string = ""): void {
        this.parent.append("circle")
            .attr("class", "needle-center")
            .attr("cx", 0)
            .attr("cy", 0)
            .attr("r", this.radius);
        if (perc >= 0 && tValue) {
            this.parent.append("path")
                .attr("class", "needle")
                .attr("d", this.rotate(perc));
        }
        this.valueText = this.parent.append("text");
        this.valueText
            .attr("x", 20)
            .attr("y", 50)
            .attr("class", "valueText")
            .attr("fill", "darkgray");
        if (tValue) {
            this.updateTextValue(`${parseFloat("" + tValue).toFixed(2)} ${unitOfMeasure}`);
        } else {
            this.updateTextValue("N/D");
        }

    }

    /**
     *  perform the animation of the element to the specified percentage
     *
     * @param perc
     */
    public animateOn(perc: number): void {
        this.parent.transition()
            .duration(1500)
            .selectAll(".needle")
            .attr("d", this.rotate(perc));
    }

    /**
     * Updates the element text value
     *
     * @param {string} text
     */
    public updateTextValue(text: string): void {
        this.parent.select(".valueText")
            .attr("text-anchor", "middle")
            .text(text);
        this.parent.select(".valueText")
            .attr("x", 0);
    }

    /**
     *  Rotates the needle to a specified percentage
     *
     * @param perc
     * @returns {string} d dimension
     */
    private rotate(perc: number): string {
        const thetaRad: number = AngleConverter.percToRad(perc / 2);
        const centerX: number = 0;
        const centerY: number = 0;
        const topX: number = centerX - this.len * Math.cos(thetaRad);
        const topY: number = centerY - this.len * Math.sin(thetaRad);
        const leftX: number = centerX - this.radius * Math.cos(thetaRad - Math.PI / 2);
        const leftY: number = centerY - this.radius * Math.sin(thetaRad - Math.PI / 2);
        const rightX: number = centerX - this.radius * Math.cos(thetaRad + Math.PI / 2);
        const rightY: number = centerY - this.radius * Math.sin(thetaRad + Math.PI / 2);
        return "M " + leftX + " " + leftY + " " + topX + " " + topY + " " + rightX + " " + rightY;
    }
}
